// Gestion des appareils connectés : enregistrement de l'appareil courant,
// liste en temps réel, révocation à distance, et vérification par code email
// pour tout appareil non reconnu (device fingerprinting léger + trust côté serveur).
//
// Modèle : chaque appareil (identifié par un UUID persistant en localStorage,
// propre au navigateur/app) est stocké dans users/{uid}/devices/{deviceId}.
// Le champ `trusted` et le sous-objet `verification` (code hashé) ne sont
// modifiables QUE par le serveur (Admin SDK, voir server-prod.js /api/device/*) —
// les règles Firestore interdisent au client de les écrire directement, sans
// quoi n'importe qui pourrait s'auto-déclarer "trusted" depuis la console.
// La révocation supprime le document ; l'appareil concerné écoute son propre
// document via onSnapshot (voir AuthContext) et se déconnecte dès que celui-ci
// disparaît. Pas de dépendance externe (pas de FingerprintJS/MaxMind/GeoIP) : le
// signal de risque retenu est uniquement "ce Device ID a-t-il déjà été validé ?" —
// pas de scoring VPN/pays, qui nécessiterait un service tiers payant.

import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../lib/firebase';
import { API_BASE_URL } from '../config/api';

export type DeviceType = 'desktop' | 'tablet' | 'mobile';

export interface DeviceRecord {
  id: string;
  label: string;
  browser: string;
  os: string;
  deviceType: DeviceType;
  model: string | null;
  trusted?: boolean;
  /** Posé uniquement par /api/device/mark-impersonated-trusted (session admin via impersonate.mjs). */
  trustedVia?: 'impersonate';
  createdAt: Timestamp | null;
  lastActiveAt: Timestamp | null;
}

const DEVICE_ID_KEY = 'fp_device_id';

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Mémorisé en mémoire dès la première résolution réussie (localStorage,
// sessionStorage, ou génération pure) — SANS ce cache, getDeviceId() relit
// localStorage à CHAQUE appel (login, puis chaque requête via
// src/lib/deviceIdFetch.ts). Si localStorage jette ne serait-ce qu'une fois
// (stockage partitionné/mode privé strict/extension/quota) sur un seul de ces
// appels, le catch régénérait jusque-là un ID aléatoire jamais persisté à
// chaque fois — le login écrivait `trusted:true` en base pour l'ID A, la
// requête suivante envoyait l'ID B (jamais vu du backend) → 403
// DEVICE_NOT_TRUSTED en boucle malgré un compte authentifié et réellement
// trusted. Ce cache garantit qu'un seul et même ID est utilisé pour tous les
// appels de la page, quel que soit l'état du storage.
let cachedDeviceId: string | null = null;

/** UUID persistant représentant cet appareil/navigateur (créé une seule fois). */
export function getDeviceId(): string {
  if (cachedDeviceId) return cachedDeviceId;

  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = generateId();
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    cachedDeviceId = id;
    return id;
  } catch {
    // localStorage inaccessible : on retente sessionStorage (survit au moins
    // aux navigations dans le même onglet) avant de se rabattre sur un ID
    // purement en mémoire — dans tous les cas, mis en cache ci-dessus pour
    // rester stable sur toute la durée de vie de la page.
    try {
      let id = window.sessionStorage.getItem(DEVICE_ID_KEY);
      if (!id) {
        id = generateId();
        window.sessionStorage.setItem(DEVICE_ID_KEY, id);
      }
      cachedDeviceId = id;
      return id;
    } catch {
      cachedDeviceId = generateId();
      return cachedDeviceId;
    }
  }
}

function detectOS(ua: string): string {
  if (/windows/i.test(ua)) return 'Windows';
  if (/ipad/i.test(ua)) return 'iPadOS';
  if (/iphone|ipod/i.test(ua)) return 'iOS';
  if (/mac os x/i.test(ua)) return 'macOS';
  if (/android/i.test(ua)) return 'Android';
  if (/linux/i.test(ua)) return 'Linux';
  return 'Inconnu';
}

function detectBrowser(ua: string): string {
  if (/edg\//i.test(ua)) return 'Edge';
  if (/opr\/|opera/i.test(ua)) return 'Opera';
  if (/(chrome|crios)\//i.test(ua) && !/edg\//i.test(ua)) return 'Chrome';
  if (/(firefox|fxios)\//i.test(ua)) return 'Firefox';
  if (/safari\//i.test(ua) && !/(chrome|crios|android)/i.test(ua)) return 'Safari';
  return 'Navigateur';
}

function detectDeviceType(ua: string): DeviceType {
  if (/ipad/i.test(ua) || (/android/i.test(ua) && !/mobile/i.test(ua)) || /tablet|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobi|iphone|ipod|android/i.test(ua)) return 'mobile';
  return 'desktop';
}

/** Modèle d'appareil déductible du User-Agent, quand le navigateur l'expose. */
function detectDeviceModel(ua: string): string | null {
  if (/ipad/i.test(ua)) return 'iPad';
  if (/iphone/i.test(ua)) return 'iPhone';
  const androidModel = ua.match(/;\s*([A-Za-z0-9][A-Za-z0-9 _-]*)\s+Build\//i);
  if (androidModel?.[1]) return androidModel[1].trim();
  return null;
}

export function getCurrentDeviceInfo() {
  const ua = navigator.userAgent || '';
  const os = detectOS(ua);
  const browser = detectBrowser(ua);
  const deviceType = detectDeviceType(ua);
  const model = detectDeviceModel(ua);
  const label = model ? `${model} · ${browser}` : `${browser} · ${os}`;
  return { os, browser, deviceType, model, label };
}

function devicesCol(uid: string) {
  return collection(db, 'users', uid, 'devices');
}

/** Crée/rafraîchit le document de l'appareil courant (appelé à chaque connexion). */
export async function registerCurrentDevice(uid: string): Promise<string> {
  const deviceId = getDeviceId();
  const info = getCurrentDeviceInfo();
  const ref = doc(devicesCol(uid), deviceId);
  await setDoc(
    ref,
    {
      ...info,
      lastActiveAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );
  return deviceId;
}

/**
 * Liste temps réel des appareils connectés, triés par activité récente.
 * Les appareils marqués `trustedVia: 'impersonate'` (sessions support admin via
 * impersonate.mjs) sont volontairement exclus — invisibles pour l'utilisateur,
 * par design (voir proxy-server/server-prod.js /api/device/mark-impersonated-trusted).
 */
export function subscribeToDevices(uid: string, callback: (devices: DeviceRecord[]) => void) {
  const q = query(devicesCol(uid), orderBy('lastActiveAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const devices = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as DeviceRecord[];
    callback(devices.filter((d) => d.trustedVia !== 'impersonate'));
  });
}

/**
 * Surveille le document de CET appareil : si un autre onglet/appareil le supprime
 * (révocation), déclenche `onRevoked`. Ne se déclenche que sur une vraie disparition
 * après avoir constaté que le document existait (évite un faux positif pendant
 * l'enregistrement initial).
 */
export function watchCurrentDeviceRevocation(uid: string, onRevoked: () => void) {
  const deviceId = getDeviceId();
  const ref = doc(devicesCol(uid), deviceId);
  let sawExisting = false;
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      sawExisting = true;
      return;
    }
    if (sawExisting) {
      onRevoked();
    }
  });
}

/** Révoque un appareil (déconnexion à distance quasi instantanée). */
export async function revokeDevice(uid: string, deviceId: string): Promise<void> {
  await deleteDoc(doc(devicesCol(uid), deviceId));
}

// ────────────────────────────────────────────────────────────────
// Vérification serveur (risk check + code email pour appareil inconnu)
// ────────────────────────────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Utilisateur non authentifié');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export interface DeviceEvaluationResult {
  trusted: boolean;
  requiresCode?: boolean;
  cooldownSeconds?: number;
}

/**
 * À appeler juste après une connexion réussie : le serveur regarde si ce
 * Device ID est déjà "trusted". Si oui → accès direct, aucun email. Si non
 * (nouvel appareil, ou vérification jamais terminée) → envoie un code à
 * l'email du compte et renvoie requiresCode:true.
 */
export async function evaluateDevice(deviceId: string): Promise<DeviceEvaluationResult> {
  const info = getCurrentDeviceInfo();
  const res = await fetch(`${API_BASE_URL}/api/device/evaluate`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ deviceId, ...info }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Erreur lors de la vérification de l'appareil");
  return data;
}

/** Renvoie un nouveau code (bouton "Renvoyer" dans la modale de vérification). */
export async function resendDeviceCode(deviceId: string): Promise<DeviceEvaluationResult> {
  const res = await fetch(`${API_BASE_URL}/api/device/resend-code`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ deviceId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Erreur lors du renvoi du code');
  return data;
}

/** Valide le code reçu par email — marque l'appareil comme "trusted" côté serveur. */
export async function verifyDeviceCode(deviceId: string, code: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE_URL}/api/device/verify-code`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ deviceId, code }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(data.error || 'Code invalide'), { attemptsRemaining: data.attemptsRemaining });
  }
  return data;
}

/**
 * Réservé à src/pages/AdminAuth.tsx, juste après un signInWithCustomToken émis
 * par proxy-server/impersonate.mjs. Marque définitivement cet appareil trusted
 * (indépendamment du claim `impersonated`, qui disparaît au prochain rafraîchissement
 * du token) — sans quoi un rechargement de page après expiration du claim
 * redéclencherait le code email vers le vrai propriétaire du compte. Le serveur
 * revérifie le claim lui-même avant d'écrire quoi que ce soit (403 sinon).
 */
export async function markImpersonatedDeviceTrusted(deviceId: string): Promise<void> {
  const info = getCurrentDeviceInfo();
  const res = await fetch(`${API_BASE_URL}/api/device/mark-impersonated-trusted`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ deviceId, ...info }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Erreur lors du marquage de l'appareil");
  }
}
